import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { MediaService } from './media.service';

import { BaseClass } from '../shared/base.class';

declare let PexRTC: any;

export type PexipCallType =
    | 'video'
    | 'presentation'
    | 'audioonly'
    | 'recvonly'
    | 'rtmp'
    | 'stream'
    | 'none';

export interface IPexipEvent {
    /** Type of event */
    type: 'disconnected' | 'end';
    /** Message */
    message?: any;
}

export interface IPexipCallOptions {
    /** Pexip conference node. e.g. test.pexip.com.au/conference */
    node: string;
    /** ID of the conference */
    conference_id: string;
    /** Name of the participant */
    name: string;
    /** Pin for the call */
    pin?: string;
    /** Maximum bandwidth (in kbps) for up and down stream (can be null) */
    bandwidth?: number;
    /** Type of connection to use for the call */
    call_type?: PexipCallType;
}

export interface IPexipMessage {
    /** Type of message */
    message_type:
        | 'connect'
        | 'error'
        | 'roster'
        | 'disconnect'
        | 'resume'
        | 'setup'
        | 'refresh'
        | 'participant_update'
        | 'participant_delete'
        | 'screenshare'
        | 'screenshare_stop'
        | 'presentation_preparing'
        | 'presentation_connect'
        | 'presentation_disconnect'
        | 'end';
    /** Pexip call stream */
    stream?: MediaStream | URL;
    /** Whether there is a pin required for connecting */
    pin_status?: 'none' | 'required' | 'optional';
    /**  */
    conference_extension?: string;
    /** Presenter User Name */
    presenter?: string;
    /** Description of the error that occurred */
    error?: string;
    /** Reason for disconnecting from the call */
    reason?: string;
    /** List of participants in the conference */
    roster?: any;
    /** Participant object */
    participant?: any;
}

@Injectable({
    providedIn: 'root',
})
export class PexipService extends BaseClass {
    /** Pexip object instance */
    private rtc: any;
    /** Subject for whether the user is in a Pexip Call */
    private _in_call = new BehaviorSubject<boolean>(false);
    /** Subject for whether audio is enabled */
    private _audio = new BehaviorSubject<boolean>(false);
    /** Subject for whether video is enabled */
    private _video = new BehaviorSubject<boolean>(false);
    /** Subject for events on the pexip object */
    private _message = new Subject<IPexipMessage>();
    /** Whether a microphone is available to use for calls */
    private _has_audio: boolean;
    /** Whether a camera is available to use for calls */
    private _has_video: boolean;
    /** Pin for the active pexip call */
    private _pin: string;
    /** Participants in the pexip call */
    private _participants = new BehaviorSubject([]);
    private _sources: Record<string, string> = {};

    /** Whether the host has muted the audio */
    public host_muted_audio: boolean;

    constructor(private _media: MediaService) {
        super();
        this.checkHardware();
    }

    /** Whether the user is in a call */
    public get in_call(): boolean {
        return this._in_call.value;
    }
    /** Whether a microphone is available to use for calls */
    public get has_audio(): boolean {
        return this._has_audio;
    }
    /** Whether a camera is available to use for calls */
    public get has_video(): boolean {
        return this._has_video;
    }
    /** Whether a microphone is currently muted */
    public get muted_audio(): boolean {
        return !this._audio.value;
    }
    /** Whether a camera stream is currently muted */
    public get muted_video(): boolean {
        return !this._video.value;
    }

    /** UUID of current user */

    public get uuid(): string | undefined {
        return this.rtc?.uuid;
    }

    /** Observable for pexip participants */
    public get participants(): Observable<any[]> {
        return this._participants.asObservable();
    }

    /** Toggle the muted state of the microphone */
    public setAudioMute(state: boolean = this._audio.getValue()) {
        if (this.rtc) this._audio.next(!this.rtc.muteAudio(state));
    }

    /** Toggle the muted state of the camera */
    public setVideoMute(state: boolean = this._video.getValue()) {
        if (this.rtc) this._video.next(!this.rtc.muteVideo(state));
    }

    /** Handle host intiated mute */
    public hostMute() {
        if (this.rtc) {
            this._audio.next(false);
            this.host_muted_audio = true;
        }
    }

    /** Handle host intiated mute */
    public hostUnMute() {
        if (this.rtc) {
            this._audio.next(true);
            this.host_muted_audio = false;
        }
    }

    /** Type of available call for pexip */
    public get call_type(): PexipCallType {
        return this.has_video ? 'video' : this.has_audio ? 'audioonly' : 'recvonly';
    }

    /** Obserable for meeting events */
    public get events(): Observable<IPexipMessage> {
        return this._message.asObservable();
    }

    /** Join pexip meeting */
    public async call(options: IPexipCallOptions) {
        if (!options.bandwidth) options.bandwidth = 5120;
        if (!this.rtc) {
            this.init();
        }
        const { node, conference_id, name, bandwidth, call_type, pin } = options;
        this._pin = pin;
        const saved_media = this._media.getSavedMedia();
        const devices = await this._media.enumerateDevices();
        // Check the sources exists (samsung browser device ID changes on every load)
        let exist_audio: MediaDeviceInfo;
        let exist_video: MediaDeviceInfo;
        if (saved_media?.audio_source) {
            exist_audio = devices.find(
                (el) =>
                    el.deviceId === saved_media.audio_source.deviceId ||
                    el.label === saved_media.audio_source.label
            );
        }
        if (saved_media?.video_source) {
            exist_video = devices.find(
                (el) =>
                    el.deviceId === saved_media.video_source.deviceId ||
                    el.label === saved_media.video_source.label
            );
        }
        const stream = await this._media.getUserMedia({
            audio: exist_audio,
            video: exist_video,
        });
        this.rtc.user_media_stream = stream;
        if (this._in_call.getValue()) {
            console.log('Call Transfer');
            this.rtc.transferParticipant(this.uuid, conference_id, 'guest', pin);
        } else {
            console.log('Call Join');
            this.rtc.makeCall(node, conference_id, name, null, call_type || this.call_type);
        }
    }

    /** Manually end the active pexip call */
    public endCall(): void {
        this.clear();
        this._message.next({ message_type: 'end' });
    }

    /** Clear pexip */
    public clear(): void {
        if (this.rtc) {
            this.rtc.disconnect();
            delete this.rtc;
        }
        this._in_call.next(false);
        this._audio.next(false);
        this._video.next(false);
        this._participants.next([]);
    }

    /** Present user's screen */
    public presentScreen(): void {
        this.rtc.present('screen');
    }

    /** Stop presenting user's screen */
    public endPresentScreen(): void {
        this.rtc.present(null);
    }

    /** Apply change in selected camera/microphone */
    public async renegotiate(audio: string, video: string) {
        console.log('Renegotiate Audio', audio);
        console.log('Renegotiate Video', video);
        this._sources = { audio, video };
        console.log('Call Renegotiate', this.rtc);
        if (!this.rtc) return;
        const old_stream = this.rtc.user_media_stream;
        if (old_stream) {
            old_stream.getTracks().forEach((track) => track.stop());
        }
        const stream = await this._media.getUserMedia({
            audio: { deviceId: audio },
            video: { deviceId: video },
        });
        this.rtc.user_media_stream = stream;
        console.log('Call Renegotiate');
        this.rtc.renegotiate();
    }

    /** Send refresh event */
    public refresh(): void {
        this._message.next({ message_type: 'refresh' });
    }

    /** Check available hardware for calls */
    private async checkHardware(): Promise<void> {
        this._has_audio = this._media.has_audio;
        this._has_video = this._media.has_video;
    }

    /** Initialise the pexip object */
    public init() {
        this.rtc = new PexRTC();
        this.rtc.onSetup = (stream, pin_status, conference_extension) => {
            this._message.next({ stream, pin_status, conference_extension, message_type: 'setup' });
            this.rtc.connect(this._pin);
            console.log('Pexip Version:', this.rtc.version);
        };
        this.rtc.onConnect = (stream) => {
            if (this._in_call.getValue()) {
                console.log('Audio State:', this._audio.getValue(), this.rtc.muted_audio);
                this.checkHardware();
                this.setAudioMute(!this._audio.getValue());
                this.setVideoMute(!this._video.getValue());
            } else {
                this.setAudioMute(false);
                this.setVideoMute(false);
            }
            this._in_call.next(true);
            this._message.next({ stream, message_type: 'connect' });
        };
        this.rtc.onError = (error) => {
            this.clear();
            this._message.next({ error, message_type: 'error' });
        };
        this.rtc.onDisconnect = (reason) => {
            this.clear();
            this._message.next({ reason, message_type: 'disconnect' });
        };
        this.rtc.onScreenshareConnected = (stream) => {
            this._message.next({ stream, message_type: 'screenshare' });
        };
        this.rtc.onScreenshareStopped = (reason) => {
            this._message.next({ reason, message_type: 'screenshare_stop' });
        };
        this.rtc.onPresentation = (setting, presenter, uuid) => {
            if (setting) {
                this._message.next({ presenter, message_type: 'presentation_preparing' });
                this.rtc.getPresentation();
            }
        };
        this.rtc.onPresentationConnected = (stream) => {
            this._message.next({ stream, message_type: 'presentation_connect' });
        };
        this.rtc.onPresentationDisconnected = (reason) => {
            this._message.next({ reason, message_type: 'presentation_disconnect' });
        };
        this.rtc.onHoldResume = (msg) => this._message.next({ ...msg, message_type: 'resume' });
        this.rtc.onRosterList = (roster) => this._message.next({ roster, message_type: 'roster' });
        this.rtc.onParticipantUpdate = (participant) => {
            if (participant.service_type === 'conference') {
                let participants = this._participants.value;
                const index = participants.findIndex((el) => el.uuid === participant.uuid);

                if (index !== -1) {
                    participants[index] = participant;
                } else {
                    participants.push(participant);
                }

                this._message.next({ participant, message_type: 'participant_update' });
                this._participants.next(participants);
            }
        };
        this.rtc.onParticipantDelete = (participant) => {
            let participants = this._participants.value;
            participants.splice(
                participants.findIndex((el) => el.uuid === participant.uuid),
                1
            );
            this._participants.next(participants);
            this._message.next({ participant, message_type: 'participant_delete' });
        };
    }
}
